/*!
 * AudoChat - Dashboard
 * Connected to PostgreSQL Database
 */

import { useState, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";

// Vision UI Dashboard React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

// React icons
import { BsChatDots, BsPeople } from "react-icons/bs";
import { IoCubeOutline } from "react-icons/io5";

// API
import api from "utils/api";

function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    conversations: 0,
    customers: 0,
    items: 0,
    activeItems: 0,
  });

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      
      // Load all stats in parallel
      const [conversations, customersStats, itemsStats] = await Promise.all([
        api.getConversations().catch(() => []),
        api.getCustomerStats().catch(() => ({ total: 0, active: 0 })),
        api.getItemStats().catch(() => ({ total: 0, active: 0 })),
      ]);

      setStats({
        conversations: Array.isArray(conversations) ? conversations.length : 0,
        customers: customersStats?.total || 0,
        items: itemsStats?.total || 0,
        activeItems: itemsStats?.active || 0,
      });
    } catch (error) {
      console.error("Error loading dashboard stats:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox 
        py={3}
        sx={{
          minHeight: "calc(100vh - 200px)",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        {loading ? (
          <VuiBox display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: "400px" }}>
            <CircularProgress color="info" />
          </VuiBox>
        ) : (
          <VuiBox mb={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} xl={3}>
                <MiniStatisticsCard
                  title={{ text: "Total Conversations", fontWeight: "regular" }}
                  count={stats.conversations}
                  percentage={{ color: "info", text: "All conversations" }}
                  icon={{ color: "info", component: <BsChatDots size="22px" color="white" /> }}
                />
              </Grid>
              <Grid item xs={12} md={6} xl={3}>
                <MiniStatisticsCard
                  title={{ text: "Total Customers" }}
                  count={stats.customers}
                  percentage={{ color: "success", text: "All customers" }}
                  icon={{ color: "info", component: <BsPeople size="22px" color="white" /> }}
                />
              </Grid>
              <Grid item xs={12} md={6} xl={3}>
                <MiniStatisticsCard
                  title={{ text: "Total Items" }}
                  count={stats.items}
                  percentage={{ color: "info", text: "All items" }}
                  icon={{ color: "info", component: <IoCubeOutline size="22px" color="white" /> }}
                />
              </Grid>
              <Grid item xs={12} md={6} xl={3}>
                <MiniStatisticsCard
                  title={{ text: "Active Items" }}
                  count={stats.activeItems}
                  percentage={{ color: "success", text: "In stock" }}
                  icon={{ color: "info", component: <IoCubeOutline size="22px" color="white" /> }}
                />
              </Grid>
            </Grid>
          </VuiBox>
        )}
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
